import React__default, { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ConciergeWidgetChat } from './ConciergeWidgetChat.js';
import { useConciergeSettings } from './settings/useBrandclubSettings.js';

const ConciergeWidget = ({ widgetState, pageContext, showMaximize = true, isMaximized = false, onMaximize, onMinimize, title, isConciergeEnabled = true, disablePortal = false, }) => {
    const { isOpen, close } = widgetState;
    const widgetRef = useRef(null);
    const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 768 : false);
    const [isClosing, setIsClosing] = useState(false);
    const [shouldRender, setShouldRender] = useState(isOpen);
    // Get positioning settings from window.brandclubSettings.concierge
    const settings = useConciergeSettings();
    // Handle open/close transitions with animations
    useEffect(() => {
        if (isOpen) {
            setShouldRender(true);
            setIsClosing(false);
        }
    }, [isOpen]);
    // Animated close handler
    const handleAnimatedClose = () => {
        setIsClosing(true);
        // Wait for animation to complete before actually closing
        setTimeout(() => {
            close();
            setShouldRender(false);
            setIsClosing(false);
        }, 250); // Match animation duration
    };
    // Track window width for responsive behavior
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    // Handle escape key to close
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && isOpen && !isClosing) {
                handleAnimatedClose();
            }
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, isClosing]);
    // Handle click outside to close (desktop widget mode only - mobile and maximized have backdrops)
    useEffect(() => {
        if (!isOpen || isMobile || isMaximized || isClosing)
            return;
        const handleClickOutside = (e) => {
            // Use composedPath() to correctly detect clicks inside Shadow DOM
            const path = e.composedPath();
            // Check if click is inside the widget container (works with Shadow DOM)
            const isInsideWidget = path.some((element) => {
                var _a;
                if (element instanceof HTMLElement) {
                    // Check for the widget container class or the shadow host
                    return (((_a = element.classList) === null || _a === void 0 ? void 0 : _a.contains('concierge-widget-container')) ||
                        element.id === 'concierge-shadow-host' ||
                        element.id === 'concierge-mount');
                }
                return false;
            });
            if (isInsideWidget) {
                return; // Click was inside the widget, don't close
            }
            // Don't close if clicking on the floating button (it has its own toggle logic)
            const isOnFloatingButton = path.some((element) => {
                if (element instanceof HTMLElement) {
                    return element.getAttribute('aria-label') === 'Open Concierge';
                }
                return false;
            });
            if (isOnFloatingButton) {
                return;
            }
            handleAnimatedClose();
        };
        // Use mousedown for better UX (closes immediately on click, not on release)
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen, isMobile, isMaximized, isClosing]);
    // Prevent body scroll when widget is open on mobile or maximized
    useEffect(() => {
        if (isOpen && (isMobile || isMaximized)) {
            document.body.style.overflow = 'hidden';
        }
        else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen, isMobile, isMaximized]);
    // Hide widget when not enabled or not rendering
    if (!isConciergeEnabled || !shouldRender) {
        return null;
    }
    // Compute styles based on mode (mobile, maximized, or default widget)
    const getContainerStyles = () => {
        if (isMobile) {
            return {
                position: 'fixed',
                zIndex: settings.z_index,
                bottom: 0,
                right: 0,
                left: 0,
                height: 'calc(100vh - env(safe-area-inset-top) - env(safe-area-inset-bottom))',
                maxHeight: 'calc(100vh - 80px)',
                width: '100%',
                minWidth: '100%',
                maxWidth: '100%',
                backgroundColor: 'white',
                borderRadius: '16px 16px 0 0',
                boxShadow: '0 25px 50px -12px rgb(0 0 0 / 0.25)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            };
        }
        if (isMaximized) {
            return {
                position: 'fixed',
                zIndex: settings.z_index,
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 'min(900px, 90vw)',
                height: 'min(700px, 85vh)',
                maxWidth: '90vw',
                maxHeight: '85vh',
                backgroundColor: 'white',
                borderRadius: '16px',
                boxShadow: '0 25px 50px -12px rgb(0 0 0 / 0.25)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            };
        }
        // Default widget mode - position based on alignment setting
        // Position widget above the floating button (60px button + 12px gap)
        const buttonHeight = 60;
        const gap = 12;
        const bottomOffset = settings.vertical_padding + buttonHeight + gap;
        const positionStyles = settings.alignment === 'left'
            ? { left: settings.horizontal_padding, right: 'auto' }
            : { right: settings.horizontal_padding, left: 'auto' };
        return Object.assign(Object.assign({ position: 'fixed', zIndex: settings.z_index, bottom: bottomOffset }, positionStyles), { height: '600px', maxHeight: `calc(100vh - ${bottomOffset + settings.vertical_padding}px)`, width: '400px', minWidth: '400px', maxWidth: '400px', backgroundColor: 'white', borderRadius: '16px', boxShadow: '0 25px 50px -12px rgb(0 0 0 / 0.25)', display: 'flex', flexDirection: 'column', overflow: 'hidden' });
    };
    // Determine animation class based on mode and closing state
    const getAnimationClass = () => {
        if (isClosing) {
            if (isMaximized) {
                return 'tw:animate-modal-collapse';
            }
            if (isMobile) {
                return 'tw:animate-slide-down-mobile';
            }
            return 'tw:animate-slide-down';
        }
        if (isMaximized) {
            return 'tw:animate-modal-expand';
        }
        return 'tw:animate-slide-up';
    };
    // Determine backdrop animation class
    const getBackdropAnimationClass = () => {
        if (isClosing) {
            return 'tw:animate-fade-out';
        }
        return 'tw:animate-fade-in';
    };
    const widget = (React__default.createElement(React__default.Fragment, null,
        (isMobile || isMaximized) && (React__default.createElement("div", { className: `tw:fixed tw:inset-0 ${getBackdropAnimationClass()}`, style: {
                position: 'fixed',
                inset: 0,
                backgroundColor: isMaximized ? 'rgba(0, 0, 0, 0.6)' : 'rgba(0, 0, 0, 0.5)',
                zIndex: settings.z_index - 1,
            }, onClick: isClosing ? undefined : (isMaximized ? onMinimize : handleAnimatedClose) })),
        React__default.createElement("div", { ref: widgetRef, className: `concierge-widget-container tw:fixed tw:bg-white tw:shadow-2xl tw:flex tw:flex-col tw:overflow-hidden ${getAnimationClass()}`, style: getContainerStyles() },
            React__default.createElement(ConciergeWidgetChat, { widgetState: Object.assign(Object.assign({}, widgetState), { close: handleAnimatedClose }), pageContext: pageContext, showMaximize: showMaximize && !isMobile, isMaximized: isMaximized, onMaximize: onMaximize, onMinimize: onMinimize, title: title })),
        React__default.createElement("style", null, `
        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(1rem);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes slide-down {
          from {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
          to {
            opacity: 0;
            transform: translateY(1rem) scale(0.95);
          }
        }
        @keyframes slide-down-mobile {
          from {
            opacity: 1;
            transform: translateY(0);
          }
          to {
            opacity: 0;
            transform: translateY(100%);
          }
        }
        @keyframes modal-expand {
          from {
            opacity: 0;
            transform: translate(-50%, -50%) scale(0.9);
          }
          to {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1);
          }
        }
        @keyframes modal-collapse {
          from {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1);
          }
          to {
            opacity: 0;
            transform: translate(-50%, -50%) scale(0.9);
          }
        }
        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        @keyframes fade-out {
          from {
            opacity: 1;
          }
          to {
            opacity: 0;
          }
        }
        .tw\\:animate-slide-up {
          animation: slide-up 0.3s ease-out forwards;
        }
        .tw\\:animate-slide-down {
          animation: slide-down 0.25s ease-in forwards;
        }
        .tw\\:animate-slide-down-mobile {
          animation: slide-down-mobile 0.25s ease-in forwards;
        }
        .tw\\:animate-modal-expand {
          animation: modal-expand 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        .tw\\:animate-modal-collapse {
          animation: modal-collapse 0.25s ease-in forwards;
        }
        .tw\\:animate-fade-in {
          animation: fade-in 0.2s ease-out forwards;
        }
        .tw\\:animate-fade-out {
          animation: fade-out 0.2s ease-in forwards;
        }
      `)));
    // Either render directly or via portal
    if (disablePortal) {
        return widget;
    }
    return createPortal(widget, document.body);
};

export { ConciergeWidget };
//# sourceMappingURL=ConciergeWidget.js.map
